/**
 * Mic capture for the canvas prompt. Browsers record webm/ogg/mp4 depending on
 * vendor; Gemini reliably accepts WAV, so recordings are re-encoded to 16 kHz mono PCM.
 */

export const MIC_MAX_SECONDS = 60;
const TARGET_RATE = 16000;

export type MicRecording = { base64: string; mimeType: 'audio/wav'; durationSeconds: number };

export type MicSession = {
  stop: () => Promise<MicRecording>;
  cancel: () => void;
};

export async function startMicRecording(onAutoStop?: () => void): Promise<MicSession> {
  if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
    throw new Error('Este navegador não permite gravar áudio.');
  }
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const recorder = new MediaRecorder(stream);
  const chunks: Blob[] = [];
  recorder.ondataavailable = (e) => {
    if (e.data.size) chunks.push(e.data);
  };
  const releaseTracks = () => stream.getTracks().forEach((t) => t.stop());
  const limit = window.setTimeout(() => onAutoStop?.(), MIC_MAX_SECONDS * 1000);
  recorder.start(250);

  const finished = new Promise<Blob>((resolve) => {
    recorder.onstop = () => resolve(new Blob(chunks, { type: recorder.mimeType || 'audio/webm' }));
  });

  return {
    async stop() {
      window.clearTimeout(limit);
      if (recorder.state !== 'inactive') recorder.stop();
      const blob = await finished;
      releaseTracks();
      if (!blob.size) throw new Error('Nenhum áudio captado.');
      return encodeWav16k(blob);
    },
    cancel() {
      window.clearTimeout(limit);
      if (recorder.state !== 'inactive') recorder.stop();
      releaseTracks();
    },
  };
}

async function encodeWav16k(blob: Blob): Promise<MicRecording> {
  const ctx = new AudioContext();
  let decoded: AudioBuffer;
  try {
    decoded = await ctx.decodeAudioData(await blob.arrayBuffer());
  } finally {
    void ctx.close();
  }
  const seconds = Math.min(decoded.duration, MIC_MAX_SECONDS);
  const frames = Math.max(1, Math.ceil(seconds * TARGET_RATE));
  const offline = new OfflineAudioContext(1, frames, TARGET_RATE);
  const src = offline.createBufferSource();
  src.buffer = decoded;
  src.connect(offline.destination);
  src.start();
  const rendered = await offline.startRendering();
  const pcm = rendered.getChannelData(0);

  const buffer = new ArrayBuffer(44 + pcm.length * 2);
  const view = new DataView(buffer);
  const writeStr = (off: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i));
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + pcm.length * 2, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, TARGET_RATE, true);
  view.setUint32(28, TARGET_RATE * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeStr(36, 'data');
  view.setUint32(40, pcm.length * 2, true);
  for (let i = 0; i < pcm.length; i++) {
    const s = Math.max(-1, Math.min(1, pcm[i]));
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }

  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 0x8000)));
  }
  return { base64: btoa(binary), mimeType: 'audio/wav', durationSeconds: seconds };
}
